// API contract types for the CBCC (co-branded credit card) flow that powers
// the /travel-credit-card application journey. Mirrors the backend DTOs 1:1 —
// see the CBCC API contract reference. Landing-page *content* is served as
// static copy (content/content.ts), NOT from these endpoints; the endpoints
// only return user-specific data.

// ---------------------------------------------------------------------------
// 1. GET /fintech/v1/cbcc/cvp — per-user landing state
// ---------------------------------------------------------------------------

export interface CvpResponse {
	data: {
		screenData: ScreenData[];
	};
}

export interface ScreenData {
	name: string;
	headerText: string;
	headerImg?: string;
	cardBackImg?: string;
	rupayCardBackImg?: string;
	visaCardBackImg?: string;
	userName?: string;
	poweredByImg?: string;
	userFlow?: string;
	ctaText?: string;
	userEligibleToApply?: boolean;
	subText?: string;
	status?: string;
	subSections?: SubSection[];
	ineligibleLogo?: string;
	ineligibleText?: string;
	ineligibleSubText?: string;
	showResumePopup?: boolean;
	disclaimer?: string;
}

export interface SubSection {
	headerText?: string;
	subText?: string;
	logo?: string;
	question?: string;
	answer?: string | TrustedHTML;
}

/**
 * Known `userFlow` values returned by CVP. Drives which bank handoff the
 * bottom CTA performs. Kept as a union of string literals for ergonomics, but
 * compared loosely since the backend owns the source of truth.
 */
export const CBCC_USER_FLOW = {
	ONBOARDING: 'ONBOARDING',
	/** Real wire value for the onboarding flow (contract said ONBOARDING). */
	ONBOARDING_PENDING: 'ONBOARDING_PENDING',
	APPROVED: 'APPROVED',
	APPROVED_NO_POPUP: 'APPROVED_NO_POPUP',
	/** New applicant — CTA routes to the /travel-credit-card/apply form. */
	REDIRECT_TO_FORM: 'REDIRECT_TO_FORM',
	/** APPROVED, but the bank returned NO management redirect — the Manage Card
	 *  CTA is suppressed and the approval-confirmation card (ineligible* fields)
	 *  shows as the second screen instead. See isApprovedNoRedirection. */
	NO_REDIRECTION: 'NO_REDIRECTION',
} as const;

/** The CVP screen that carries the CTA state (ctaText/userFlow/eligibility). */
export const HOME_SCREEN_NAME = 'HOME_SCREEN';

export type CbccUserFlow = (typeof CBCC_USER_FLOW)[keyof typeof CBCC_USER_FLOW];

// ---------------------------------------------------------------------------
// 2. POST /fintech/v1/feature-validity/pincode — delivery + secured split
// ---------------------------------------------------------------------------

export interface PincodeEligibilityInput {
	/** Always "CBCC". */
	feature: string;
	/** 6-digit pincode. */
	pincode: string;
}

/** Wire shape — the payload is wrapped in a `data` envelope (like CVP). */
export interface PincodeEligibilityResponse {
	data: PincodeEligibilityData;
}

export interface PincodeEligibilityData {
	/** The pincode gate: true = card can be delivered, regardless of variant. */
	valid: boolean;
	secured: boolean;
	unSecured: boolean;
	/** Secured-card explainer content — present only when relevant. */
	metadata?: SecuredCardMetadata;
}

export interface SecuredCardMetadata {
	headerText: string;
	subSections: {
		title: string;
		description: string;
		logo: string;
	}[];
}

// ---------------------------------------------------------------------------
// 3. POST /fintech/v1/cbcc/init — application-form prefill
// ---------------------------------------------------------------------------

export interface InitRequest {
	/** Mirror the choice from pincode eligibility. */
	secured: boolean;
}

/** Wire shape — the payload is wrapped in a `data` envelope (like CVP). */
export interface InitResponseEnvelope {
	data: InitResponse;
}

export interface InitResponse {
	status: boolean;
	email: string;
	name: string;
	mobile: string;
	/**
	 * Display label → submit value, e.g. `{ "Salaried": "SALARIED" }`.
	 * The value (right side) is what `ApplyRequest.employmentType` expects.
	 */
	employmentTypes: EmploymentTypes;
	ctaText: string;
	capturePan: boolean;
	secured?: boolean;
}

export type EmploymentTypes = Record<string, string>;

// ---------------------------------------------------------------------------
// 4. POST /fintech/v1/cbcc/apply — submit application & get bank redirect
// ---------------------------------------------------------------------------

export interface ApplyRequest {
	/** "MALE" | "FEMALE" | "OTHER" */
	gender: string;
	/** e.g. "+91" */
	mobileCountryCode: string;
	/** 6-digit */
	pincode: string;
	/** "YYYY-MM-DD" */
	dob: string;
	/** key from `InitResponse.employmentTypes` */
	employmentType: string;
	/** 10-digit */
	mobileNumber: string;
	name: string;
	email: string;
	secured?: boolean;
}

// ---------------------------------------------------------------------------
// 5. POST /fintech/v1/cbcc/onboard — bank redirect for already-eligible users
// ---------------------------------------------------------------------------

/** Wire shape — the payload is wrapped in a `data` envelope (like CVP). */
export interface OnboardResponseEnvelope {
	data: OnboardResponse;
}

export interface OnboardResponse {
	merchant: string;
	data: string;
	redirectionUrl: string;
	[key: string]: unknown;
}

// ---------------------------------------------------------------------------
// 4 & 6 shared — bank redirection envelope
// ---------------------------------------------------------------------------

/** Wire shape — the payload is wrapped in a `data` envelope (like CVP).
 * NOTE: the envelope's `data` holds the response object, whose own `data`
 * field is the signed bank payload — two different `data`s. */
export interface BankRedirectionResponseEnvelope {
	data: BankRedirectionResponse;
}

/**
 * Returned by /apply and /hf/manage/card. The handoff differs by endpoint:
 * - /apply & /onboard → hidden-form POST of `merchant` + `data` to
 *   `redirectionUrl` (the signed payload authorizes the handoff).
 * - /hf/manage/card → plain GET redirect to `redirectUrl`.
 *
 * `redirectToBank` is part of the contract but unused on the client — the gate
 * is the presence of `redirectionUrl` / `redirectUrl`.
 */
export interface BankRedirectionResponse {
	merchant: string;
	data: string;
	redirectToBank: boolean;
	redirectionUrl: string;
	redirectUrl: string;
}
